/* eslint-disable */
import React, { useState, useSyncExternalStore, useMemo } from 'react';
import GenericCard from '@ui-components/Card/GenericCard';
import { GenericTable, GenericTableColumn } from '@ui-components/Table/GenericTable';
import { 
  CloudPropagationNeighbor, 
  CloudPropagationApiResponse,
  CloudPropagationRuleRow,
  BgpNeighborAssociation,
  cloudPropagationRuleColumns,
  transformToGroupedRuleRows,
  transformGroupedApiResponseToRuleRows,
  transformNewApiResponseToRuleRows,
  transformInstancePropagationRulesToRuleRows,
  CloudPropagationRule,
  GroupedPropagationRuleApiItem,
  NewPropagationRuleApiItem,
  InstancePropagationRuleItem,
} from './CloudPropagationData';
import { transformCloudPropagationData, transformClusterCloudPropagationData } from '../../../helpers/formattersShapedData';
import { ReactComponent as IconEdit } from '../../../../shared/assets/Icons/Icon_Edit.svg';
import { ReactComponent as IconDelete } from '../../../../shared/assets/Icons/Icon_Delete.svg';
import GenericButton from '@ui-components/Button/GenericButton';
import CPModal from './CPModal/CPModal';
import GenericAlert from '@ui-components/Alert/GenericAlert';
import { useAppSelector } from '@redux/hooks';
import { buildInboundPropagationRulePayload, createInboundPropagationRule, createOutboundPropagationRule, updateInboundPropagationRule, updateOutboundPropagationRules, deletePropagationRule, getCloudPropagationRuleDetails } from '../request/request';
import { updateInboundPropagationRuleCluster, updateOutboundPropagationRulesCluster, createInboundPropagationRuleCluster, createOutboundPropagationRuleCluster, getCloudPropagationRulesByCluster, deleteInboundPropagationRuleCluster, deletePropagationRuleCluster } from '../Cluster/request/request';
import {
  getSuccessAlertData,
} from '../../../helpers/cloudPropagationHelpers';
import { TagsWithPopover } from 'kontrolEdge/components/Shared/TagsWithPopover/TagsWithPopover';
import { TableSkeleton } from '@ui-components/Skeleton/GenericSkeleton';
import { DataStateView } from 'kontrolEdge/components/Backbone/BackboneDetails/StateViews';
import { describeCapSource, resolveInstanceCaps } from '../../../helpers/instanceLimits';
import {
  getTableBodyMaxHeight,
  getInitialRowsPerPage,
  REFLECTOR_V2_ROWS_PER_PAGE_OPTIONS,
} from '../../../helpers/tableViewport';

/** SideNav + detail tabs + title/Add-rule row + card padding (CTX-331). */
const CLOUD_PROPAGATION_CHROME_PX = 400;

// Tailwind breakpoints: sm 640, md 768, lg 1024, xl 1280, 2xl 1536
const XL_PX = 1280;
const XXL_PX = 1536;

function getBgpNeighborsMaxVisible(): number {
  if (typeof window === 'undefined') return 2;
  const w = window.innerWidth;
  if (w < XL_PX) return 1;   // sm, md, lg: 1 tag + "+N"
  if (w < XXL_PX) return 2;  // xl: 2 tags
  return 3;                  // 2xl+: 3 tags
}

function subscribeToResize(cb: () => void): () => void {
  window.addEventListener('resize', cb);
  return () => window.removeEventListener('resize', cb);
}

function useBgpNeighborsMaxVisible(): number {
  return useSyncExternalStore(
    subscribeToResize,
    getBgpNeighborsMaxVisible,
    () => 2
  );
}

interface CloudPropagationPageProps {
  data?: CloudPropagationNeighbor[] | CloudPropagationApiResponse;
  columns: GenericTableColumn<CloudPropagationNeighbor>[];
  error: string | null;
  onConfigure?: (row: CloudPropagationNeighbor) => void;
  getAccessTokenSilently?: () => Promise<string>;
  clusterId?: number; // Add clusterId for cluster mode
  reflectorId?: number; // Add reflectorId for standalone mode
  onRefresh?: () => void | Promise<void>; // Callback to refresh table data
  /** When true, parent is fetching data. First load shows spinner overlay; secondary loads show table skeletons. */
  loading?: boolean;
  /** From GET bgp-config `reflectorInstance.instanceLimits` — caps propagation rule count via `cpr_limit` / map. */
  instanceLimits?: unknown;
  // True when the associated reflector (standalone) or any instance in the cluster (cluster mode)
  // is below DYNAMIC_TAG_MIN_VERSION — disables the dynamic-tag-based CPR options (CTX-163).
  disableDynamicTagOptions?: boolean;
  // Same, for the inbound "Advanced settings → Retry" block below NEXTHOP_RETRY_MIN_VERSION (CTX-181).
  disableNexthopRetry?: boolean;
  // ≥ NEXTHOP_WATCH_MIN_VERSION: Retry gives way to "Next-hop verification" (CTX-347).
  useNexthopVerification?: boolean;
}

const CloudPropagationPage: React.FC<CloudPropagationPageProps> = ({
  data,
  columns,
  error,
  onConfigure,
  getAccessTokenSilently,
  clusterId,
  reflectorId,
  onRefresh,
  loading: parentLoading = false,
  instanceLimits,
  disableDynamicTagOptions,
  disableNexthopRetry,
  useNexthopVerification,
}) => {
  const { tenantId } = useAppSelector((state: any) => state.selection);
  // State for modal open/close and selected row
  const [isCPModalOpen, setIsCPModalOpen] = useState(false);
  const emptyCpInitialValues = useMemo(() => ({}), []);
  const [selectedRow, setSelectedRow] = useState<CloudPropagationNeighbor | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [loadingRowId, setLoadingRowId] = useState<number | string | null>(null);

  // First load = spinner overlay only; secondary/refresh = table skeletons
  const hasStartedLoadRef = React.useRef(false);
  const hasCompletedFirstLoadRef = React.useRef(false);
  if (parentLoading) hasStartedLoadRef.current = true;
  if (!parentLoading && hasStartedLoadRef.current) hasCompletedFirstLoadRef.current = true;
  const isFirstLoad = parentLoading && !hasCompletedFirstLoadRef.current;
  
  // Alert state variables for success notifications
  const [isAlertOpen, setIsAlertOpen] = useState(false);
  const [alertTitle, setAlertTitle] = useState("");
  const [alertDescription, setAlertDescription] = useState("");
  const [alertVariant, setAlertVariant] = useState<"success" | "error" | "confirm" | undefined>(undefined);

  // Delete confirmation state
  const [deleteConfirmState, setDeleteConfirmState] = useState<{
    isOpen: boolean;
    row: CloudPropagationNeighbor | null;
    isDeleting: boolean;
    propagationRuleId?: number | string;
  }>({
    isOpen: false,
    row: null,
    isDeleting: false,
    propagationRuleId: undefined
  });

  // Helper function to show success alerts
  const showSuccessAlert = (operation: 'create' | 'update', ruleType: 'inbound' | 'outbound', mode: 'standalone' | 'cluster') => {
    const alertData = getSuccessAlertData(operation, ruleType, mode);
    setAlertTitle(alertData.title);
    setAlertDescription(alertData.description);
    setAlertVariant(alertData.variant);
    setIsAlertOpen(true);
    
    // Auto-close alert after 3 seconds
    setTimeout(() => {
      setIsAlertOpen(false);
    }, 3000);
  };

  // Transform data to new grouped structure
  const transformedData = React.useMemo(() => {
    if (!data) return [];
    
    // Handle new grouped API structure - wrapped in data property
    if (typeof data === 'object' && 'data' in data) {
      const apiResponse = data as CloudPropagationApiResponse;
      if (Array.isArray(apiResponse.data) && apiResponse.data.length > 0) {
        const firstItem = apiResponse.data[0];
        
        // Instance list format: propagationRuleId (string UUID), associatedNeighbors, inbounds, outbounds
        if (firstItem && typeof firstItem === 'object' &&
            'propagationRuleId' in firstItem &&
            typeof (firstItem as { propagationRuleId?: unknown }).propagationRuleId === 'string' &&
            'associatedNeighbors' in firstItem) {
          return transformInstancePropagationRulesToRuleRows(apiResponse.data as unknown as InstancePropagationRuleItem[]);
        }
        
        // Check if it's the newest API format (has propagationRuleId number, propagationRuleSubUuid, associatedNeighbors)
        if (firstItem && typeof firstItem === 'object' && 
            'propagationRuleId' in firstItem && 
            'propagationRuleSubUuid' in firstItem && 
            'associatedNeighbors' in firstItem) {
          return transformNewApiResponseToRuleRows(apiResponse.data as unknown as NewPropagationRuleApiItem[]);
        }
        
        // Check if it's the grouped structure (has neighborAddress, bgpNeighbor, inboundRule, outboundRule)
        if (firstItem && typeof firstItem === 'object' && 
            'neighborAddress' in firstItem && 
            'bgpNeighbor' in firstItem && 
            ('inboundRule' in firstItem || 'outboundRule' in firstItem)) {
          // It's the grouped API response structure
          return transformGroupedApiResponseToRuleRows(apiResponse.data as unknown as GroupedPropagationRuleApiItem[]);
        }
        
        // Check if it's the old CloudPropagationRule structure (has id, bgpNeighbor, etc.)
        if (firstItem && typeof firstItem === 'object' && 'id' in firstItem && 'bgpNeighbor' in firstItem) {
          // It's CloudPropagationRule[]
          const rules = apiResponse.data as CloudPropagationRule[];
          return transformToGroupedRuleRows(rules);
        }
      }
    }
    // Handle direct array response (fallback)
    else if (Array.isArray(data)) {
      if (data.length > 0) {
        const firstItem = data[0];
        
        // Instance list format: propagationRuleId (string UUID), associatedNeighbors
        if (firstItem && typeof firstItem === 'object' &&
            'propagationRuleId' in firstItem &&
            typeof (firstItem as { propagationRuleId?: unknown }).propagationRuleId === 'string' &&
            'associatedNeighbors' in firstItem) {
          return transformInstancePropagationRulesToRuleRows(data as unknown as InstancePropagationRuleItem[]);
        }
        
        // Check if it's the newest API format (propagationRuleId number, propagationRuleSubUuid, associatedNeighbors)
        if (firstItem && typeof firstItem === 'object' && 
            'propagationRuleId' in firstItem && 
            'propagationRuleSubUuid' in firstItem && 
            'associatedNeighbors' in firstItem) {
          return transformNewApiResponseToRuleRows(data as unknown as NewPropagationRuleApiItem[]);
        }
        
        // Check if it's the grouped structure
        if (firstItem && typeof firstItem === 'object' && 
            'neighborAddress' in firstItem && 
            'bgpNeighbor' in firstItem && 
            ('inboundRule' in firstItem || 'outboundRule' in firstItem)) {
          return transformGroupedApiResponseToRuleRows(data as unknown as GroupedPropagationRuleApiItem[]);
        }
        
        // Check if it's CloudPropagationRule[]
        if (firstItem && typeof firstItem === 'object' && 'id' in firstItem && 'bgpNeighbor' in firstItem) {
          return transformToGroupedRuleRows(data as unknown as CloudPropagationRule[]);
        }
        
        // Legacy format - transform first, then extract rules
        const legacyData = clusterId 
          ? transformClusterCloudPropagationData(data)
          : transformCloudPropagationData(data);
        // For legacy data, we need to reconstruct rules from neighbors
        // This is a temporary bridge - ideally API should return rules directly
        return legacyData as any[]; // Return legacy format for now
      }
    }
    
    return [];
  }, [data, clusterId]);

  // Ensure data is always an array to prevent "data is not iterable" error
  const safeData = Array.isArray(transformedData) ? transformedData : [];

  const instanceCaps = resolveInstanceCaps(instanceLimits ?? null);
  const cprCap = instanceCaps.cprLimit;
  const capSource = describeCapSource(instanceCaps.source);
  const isAtCprLimit = safeData.length >= cprCap;
  const addRuleDisabledReason = isAtCprLimit
    ? `Rule limit reached (${safeData.length}/${cprCap} ${capSource}).`
    : undefined;

  // Data fetching is now handled in CPModal component

  // Handle delete button click - show confirmation
  const handleDeleteClick = async (row: CloudPropagationNeighbor) => {
    // For grouped structure, propagationRuleId is already available in the row
    const propagationRuleId = (row as any).propagationRuleId;
    
    if (propagationRuleId) {
      // For grouped structure, we already have the propagationRuleId
      // Show confirmation dialog immediately
      setDeleteConfirmState({
        isOpen: true,
        row: row,
        isDeleting: false,
        propagationRuleId: propagationRuleId
      });
      return;
    }
    
    // For legacy structure, fetch rule details to get propagationRuleId
    if (!getAccessTokenSilently) return;
    
    setLoadingRowId(row.bgpNeighborId || null);
    try {
      if (!tenantId) return;
      const token = await getAccessTokenSilently();
      
      let ruleIdToDelete: number | undefined = undefined;
      
      if (clusterId) {
        // Cluster mode: fetch cluster propagation rules
        if (row.bgpNeighborId) {
          const response = await getCloudPropagationRuleDetails(token, row.bgpNeighborId, tenantId);
          const dataArray = Array.isArray(response.data.data) ? response.data.data : [response.data.data];
          
          dataArray.forEach((rule: any) => {
            // Extract propagationRuleId from the rule
            if (rule.id) {
              ruleIdToDelete = rule.id;
            }
          });
        }
      } else if (row.bgpNeighborId) {
        // Standalone mode: fetch individual BGP neighbor rule details
        const response = await getCloudPropagationRuleDetails(token, row.bgpNeighborId, tenantId);
        const dataArray = Array.isArray(response.data.data) ? response.data.data : [response.data.data];
        
        dataArray.forEach((rule: any) => {
          // Extract propagationRuleId from the rule
          if (rule.id) {
            ruleIdToDelete = rule.id;
          }
        });
      }
      
      // Show confirmation dialog with rule ID
      setDeleteConfirmState({
        isOpen: true,
        row: row,
        isDeleting: false,
        propagationRuleId: ruleIdToDelete
      });
    } catch (error: any) {
      console.error('Error fetching rule details for delete:', error);
      setAlertTitle('Error');
      setAlertDescription('Failed to load rule details for deletion.');
      setAlertVariant('error');
      setIsAlertOpen(true);
      setTimeout(() => setIsAlertOpen(false), 3000);
    } finally {
      setLoadingRowId(null);
    }
  };

  // Handle delete confirmation
  const handleDeleteConfirm = async () => {
    if (!deleteConfirmState.row || !getAccessTokenSilently || !deleteConfirmState.propagationRuleId) return;
    
    setDeleteConfirmState(prev => ({ ...prev, isDeleting: true }));
    
    try {
      if (!tenantId) return;
      const token = await getAccessTokenSilently();
      const ruleId = deleteConfirmState.propagationRuleId;
      if (clusterId != null) {
        await deletePropagationRuleCluster(token, ruleId, tenantId);
      } else {
        await deletePropagationRule(token, ruleId, tenantId);
      }
      
      // Show success alert
      setAlertTitle('Propagation rule deleted successfully');
      setAlertDescription('The propagation rule has been deleted successfully.');
      setAlertVariant('success');
      setIsAlertOpen(true);
      setTimeout(() => setIsAlertOpen(false), 3000);
      
      // Close confirmation dialog
      setDeleteConfirmState({
        isOpen: false,
        row: null,
        isDeleting: false,
        propagationRuleId: undefined
      });
      
      // Refresh the table data after successful deletion
      if (onRefresh) {
        await onRefresh();
      }
    } catch (error: any) {
      console.error('Error deleting propagation rule:', error);
      setAlertTitle('Error');
      setAlertDescription(error?.response?.data?.message || 'Failed to delete propagation rule.');
      setAlertVariant('error');
      setIsAlertOpen(true);
      setTimeout(() => setIsAlertOpen(false), 3000);
      
      setDeleteConfirmState(prev => ({ ...prev, isDeleting: false }));
    }
  };

  // Handle delete cancel
  const handleDeleteCancel = () => {
    setDeleteConfirmState({
      isOpen: false,
      row: null,
      isDeleting: false,
      propagationRuleId: undefined
    });
  };

  // Handle opening the modal - data fetching is now handled in CPModal
  const handleOpenModal = (row: CloudPropagationNeighbor) => {
    // Set loading state on the button
    const rowIdentifier = (row as any).propagationRuleId || row.bgpNeighborId;
    if (rowIdentifier) {
      setLoadingRowId(rowIdentifier);
    }
    
    // Clear previous data
    setSelectedRow(null);
    
    // Set the new selected row and open modal immediately
    // The modal will handle fetching data and showing loader
    setSelectedRow(row);
    setIsCPModalOpen(true);
    
    // Clear loading state after a brief delay (modal will show its own loader)
    setTimeout(() => {
      setLoadingRowId(null);
    }, 100);
  };
  

  // Handle form submission
  const handleSubmit = async (formData: any) => {
    const isAddFlow = !selectedRow;
    if (isAddFlow && safeData.length >= cprCap) {
      setAlertTitle('Limit reached');
      setAlertDescription(
        addRuleDisabledReason ??
          `You can have at most ${cprCap} propagation rule(s) ${capSource}.`
      );
      setAlertVariant('error');
      setIsAlertOpen(true);
      setTimeout(() => setIsAlertOpen(false), 4000);
      return;
    }

    // Check if we have the required props for either cluster or standalone mode
    // For standalone mode: can use selectedRow?.bgpNeighborId (edit mode) or reflectorId (add mode)
    const isClusterMode = clusterId && getAccessTokenSilently;
    const isStandaloneMode = (selectedRow?.bgpNeighborId || reflectorId) && getAccessTokenSilently;

    if (!isClusterMode && !isStandaloneMode) {
      console.error('Missing required props for submission. Cluster mode:', isClusterMode, 'Standalone mode:', isStandaloneMode, 'reflectorId:', reflectorId, 'selectedRow:', selectedRow);
      return;
    }

    setIsSubmitting(true);
    try {
      const token = await getAccessTokenSilently();
      const { inbound, outbound, activeTab, isUpdate, isClusterMode: isClusterUpdate, ruleType, payload, batchOps } = formData;

      // Batch submit: run inbound first, then outbound. Inbound create response provides propagationRuleId for outbound payload.
      if (Array.isArray(batchOps) && batchOps.length > 0) {
        if (!tenantId) return;

        let propagationRuleIdFromInbound: string | undefined;

        const runSingle = async (op: any, outboundPropagationRuleId?: string): Promise<string | undefined> => {
          const opRuleType = op?.ruleType;
          let opPayload = op?.payload;
          const opIsUpdate = !!op?.isUpdate;
          const opIsClusterUpdate = !!formData?.isClusterMode;

          // Cluster: for PUT (update) we need propagationRuleId in the payload. Retrieve from op or selectedRow (rule can be inbound-only or outbound-only).
          const resolvedPropagationRuleId = op?.propagationRuleId ?? (selectedRow as any)?.propagationRuleId;

          // Outbound: always attach propagationRuleId when we have it (from awaited inbound create or existing)
          if (opRuleType === 'outbound' && opPayload) {
            const ruleIdToSend = outboundPropagationRuleId ?? resolvedPropagationRuleId;
            if (ruleIdToSend != null && ruleIdToSend !== '') {
              opPayload = { ...opPayload, propagationRuleId: String(ruleIdToSend) };
            }
          }

          if (opIsClusterUpdate && clusterId) {
            if (opRuleType === 'inbound' && opPayload) {
              // Update: need propagationRuleId (UUID) from op or selectedRow; include it in payload
              const propagationRuleIdForUpdate = resolvedPropagationRuleId != null ? String(resolvedPropagationRuleId) : undefined;
              if (opIsUpdate && propagationRuleIdForUpdate) {
                const payloadWithRuleId = { ...opPayload, propagationRuleId: propagationRuleIdForUpdate };
                await updateInboundPropagationRuleCluster(token, clusterId, propagationRuleIdForUpdate, payloadWithRuleId, tenantId);
                return undefined;
              }
              const res = await createInboundPropagationRuleCluster(token, clusterId, opPayload, tenantId);
              const data = res?.data;
              const ruleId = data?.data?.propagationRuleId ?? data?.propagationRuleId;
              return ruleId != null ? String(ruleId) : undefined;
            }
            if (opRuleType === 'outbound' && opPayload) {
              const propagationRuleIdForUpdate = (opPayload.propagationRuleId ?? resolvedPropagationRuleId) != null
                ? String(opPayload.propagationRuleId ?? resolvedPropagationRuleId)
                : undefined;
              if (opIsUpdate && propagationRuleIdForUpdate) {
                const payloadWithRuleId = { ...opPayload, propagationRuleId: propagationRuleIdForUpdate };
                await updateOutboundPropagationRulesCluster(token, clusterId, propagationRuleIdForUpdate, payloadWithRuleId, tenantId);
              } else {
                await createOutboundPropagationRuleCluster(token, clusterId, opPayload, tenantId);
              }
            }
            return undefined;
          }

          if (isStandaloneMode && !opIsClusterUpdate) {
            const bgpNeighborIdForPath = opPayload?.bgpNeighborIds?.[0] || selectedRow?.bgpNeighborId;
            if (opRuleType === 'inbound' && opPayload && bgpNeighborIdForPath) {
              const opPropagationRuleId = op?.propagationRuleId;
              const opInboundId = op?.inboundId;
              if (opIsUpdate && opPropagationRuleId && opInboundId) {
                await updateInboundPropagationRule(token, opPropagationRuleId, opInboundId, opPayload, tenantId);
                return undefined;
              }
              const res = await createInboundPropagationRule(token, bgpNeighborIdForPath, opPayload, tenantId);
              const ruleId = res?.data?.data?.propagationRuleId;
              return ruleId != null ? String(ruleId) : undefined;
            }
            if (opRuleType === 'outbound' && opPayload) {
              const opPropagationRuleId = opPayload.propagationRuleId ?? op?.propagationRuleId;
              if (opIsUpdate && opPropagationRuleId) {
                await updateOutboundPropagationRules(token, opPropagationRuleId, opPayload, tenantId);
              } else if (bgpNeighborIdForPath) {
                await createOutboundPropagationRule(token, bgpNeighborIdForPath, opPayload, tenantId);
              }
            }
            return undefined;
          }
          return undefined;
        };

        // Cluster mode: run inbound first, await response to get propagationRuleId (UUID), then send outbound with that UUID.
        if (formData?.isClusterMode && clusterId) {
          const inboundOp = batchOps.find((o: any) => o?.ruleType === 'inbound');
          const outboundOp = batchOps.find((o: any) => o?.ruleType === 'outbound');
          if (inboundOp) {
            propagationRuleIdFromInbound = await runSingle(inboundOp, undefined) ?? propagationRuleIdFromInbound;
          }
          if (outboundOp) {
            await runSingle(outboundOp, propagationRuleIdFromInbound);
          }
        } else {
          for (const op of batchOps) {
            propagationRuleIdFromInbound = await runSingle(op, propagationRuleIdFromInbound) ?? propagationRuleIdFromInbound;
          }
        }

        // One success alert and one close after all ops.
        setAlertTitle('Propagation rules saved successfully');
        setAlertDescription('Inbound and outbound propagation rules have been saved successfully.');
        setAlertVariant('success');
        setIsAlertOpen(true);
        setTimeout(() => setIsAlertOpen(false), 3000);

        setIsCPModalOpen(false);
        setSelectedRow(null);

        if (onRefresh) {
          await onRefresh();
        }

        return;
      }
      
      // LEGACY HANDLERS - These should not be reached since CPModal always sends batchOps
      // If we reach here, it means batchOps wasn't provided, which shouldn't happen
      console.warn('No batchOps provided - this should not happen. CPModal should always send batchOps.');
      setIsCPModalOpen(false);
      setSelectedRow(null);
    } catch (error: any) {
      console.error('Error creating propagation rule:', error);
      setAlertTitle('Error');
      setAlertDescription(
        error?.response?.data?.message || 'Failed to save propagation rules. Please try again.'
      );
      setAlertVariant('error');
      setIsAlertOpen(true);
      setTimeout(() => setIsAlertOpen(false), 4000);
      // Re-throw so CPModal's own catch keeps the modal open for retry
      // instead of closing it as if the save had succeeded (CTX-155).
      throw error;
    } finally {
      setIsSubmitting(false);
    }
  };

  // Check if we're using the new grouped structure
  const isGroupedStructure = safeData.length > 0 && 'subId' in safeData[0];

  // Responsive: sm/md/lg → 1 tag, xl → 2, 2xl+ → 3 (for BGP Neighbors column + future Name column)
  const bgpNeighborsMaxVisible = useBgpNeighborsMaxVisible();
  
  // Use new columns for grouped structure, otherwise use legacy columns
  const tableColumns = isGroupedStructure
    ? cloudPropagationRuleColumns.map((col) =>
        col.header === 'BGP Neighbors'
          ? {
              ...col,
              renderCell: (row: CloudPropagationRuleRow) => {
                const neighborTags =
                  row.bgpNeighbors?.map((n) => `${n.neighborIp} (AS ${n.peerAs})`) ?? [];
                return (
                  <TagsWithPopover
                    tags={neighborTags}
                    maxVisible={bgpNeighborsMaxVisible}
                    tagVariant="default"
                  />
                );
              },
            }
          : col.header === 'ACTIONS'
          ? {
              ...col,
              renderCell: (row: CloudPropagationRuleRow) => {
                // For grouped structure, use propagationRuleId for loading/deleting state
                const rowIdentifier = row.propagationRuleId;
                const isThisRowLoading = loadingRowId === rowIdentifier;
                // Check delete state - need to compare with propagationRuleId if available in deleteConfirmState
                const deleteRowId = (deleteConfirmState.row as any)?.propagationRuleId || deleteConfirmState.row?.bgpNeighborId;
                const isThisRowDeleting = deleteRowId === rowIdentifier && deleteConfirmState.isDeleting;
                return (
                  <div className="flex items-center gap-2">
                    <GenericButton
                      isIconOnly
                      icon={isThisRowLoading ? undefined : <IconEdit className="w-8 h-8" />}
                      variant="secondary"
                      shape="filled"
                      size="sm"
                      aria-label="Edit"
                      loading={isThisRowLoading}
                      disabled={isThisRowLoading || isThisRowDeleting}
                      onClick={() => {
                        // Convert CloudPropagationRuleRow to CloudPropagationNeighbor for modal compatibility
                        // For grouped structure, we need to pass all BGP neighbors and use propagationRuleId
                        const neighborRow: CloudPropagationNeighbor = {
                          bgpNeighborIp: row.bgpNeighbors[0]?.neighborIp || '',
                          peerAs: String(row.bgpNeighbors[0]?.peerAs || ''),
                          mode: row.mode,
                          lastUpdate: row.lastUpdate,
                          bgpNeighborId: row.bgpNeighbors[0]?.bgpNeighborId,
                          reflectorInstanceId: row.reflectorInstanceId,
                        };
                        // Store propagationRuleId for fetching rule details
                        (neighborRow as any).propagationRuleId = row.propagationRuleId;
                        // Store all BGP neighbors from the row so they can be prefilled in the modal
                        (neighborRow as any).allBgpNeighbors = row.bgpNeighbors.map((neighbor: BgpNeighborAssociation) => ({
                          id: neighbor.bgpNeighborId,
                          neighborAddress: neighbor.neighborIp,
                          peerAs: neighbor.peerAs
                        }));
                        handleOpenModal(neighborRow);
                        onConfigure?.(neighborRow);
                      }}
                    />
                    <GenericButton
                      isIconOnly
                      icon={<IconDelete className="w-6 h-6" />}
                      variant="danger"
                      shape="filled"
                      size="sm"
                      aria-label="Delete"
                      loading={isThisRowDeleting}
                      disabled={isThisRowLoading || isThisRowDeleting}
                      onClick={() => {
                        // Convert CloudPropagationRuleRow to CloudPropagationNeighbor for delete compatibility
                        // Use the first BGP neighbor for delete operations
                        const neighborRow: CloudPropagationNeighbor = {
                          bgpNeighborIp: row.bgpNeighbors[0]?.neighborIp || '',
                          peerAs: String(row.bgpNeighbors[0]?.peerAs || ''),
                          mode: row.mode,
                          lastUpdate: row.lastUpdate,
                          bgpNeighborId: row.bgpNeighbors[0]?.bgpNeighborId,
                          reflectorInstanceId: row.reflectorInstanceId,
                        };
                        // Store propagationRuleId for reference in delete operations
                        (neighborRow as any).propagationRuleId = row.propagationRuleId;
                        handleDeleteClick(neighborRow);
                      }}
                    />
                  </div>
                );
              },
            }
          : col
      )
    : columns.map((col) =>
      col.header === 'ACTIONS'
        ? {
            ...col,
            renderCell: (row: CloudPropagationNeighbor) => {
              const isThisRowLoading = loadingRowId === row.bgpNeighborId;
              const isThisRowDeleting = deleteConfirmState.row?.bgpNeighborId === row.bgpNeighborId && deleteConfirmState.isDeleting;
              return (
                <div className="flex items-center gap-2">
                  <GenericButton
                    isIconOnly
                    icon={isThisRowLoading ? undefined : <IconEdit className="w-8 h-8" />}
                    variant="secondary"
                    shape="filled"
                    size="sm"
                    aria-label="Edit"
                    loading={isThisRowLoading}
                    disabled={isThisRowLoading || isThisRowDeleting}
                    onClick={() => {
                      handleOpenModal(row);
                      onConfigure?.(row);
                    }}
                  />
                  <GenericButton
                    isIconOnly
                    icon={<IconDelete className="w-6 h-6" />}
                    variant="danger"
                    shape="filled"
                    size="sm"
                    aria-label="Delete"
                    loading={isThisRowDeleting}
                    disabled={isThisRowLoading || isThisRowDeleting}
                    onClick={() => {
                      handleDeleteClick(row);
                    }}
                  />
                </div>
              );
            },
          }
        : col
    );

  return (
    <div className="relative">
      <GenericCard isCollapsible={false} className="flex flex-col">
        <div className="flex flex-col gap-6 p-6">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-xl font-semibold">Cloud Propagation</h2>
            <div className="flex flex-col items-end gap-1">
              <GenericButton
                label="Add propagation rule"
                variant="brand"
                size="md"
                disabled={isAtCprLimit}
                title={addRuleDisabledReason}
                onClick={() => {
                  if (isAtCprLimit) return;
                  setSelectedRow(null);
                  setIsCPModalOpen(true);
                }}
              />
              {isAtCprLimit && (
                <span className="text-xs text-semantic-text-secondary text-right max-w-xs">
                  {addRuleDisabledReason}
                </span>
              )}
            </div>
          </div>

          {/* First load and refresh both use skeleton (no spinner overlay) */}
          <DataStateView
            loading={parentLoading || isFirstLoad || isSubmitting}
            error={error}
            isEmpty={safeData.length === 0}
            onRetry={onRefresh}
            loadingContent={<TableSkeleton columns={5} rows={5} />}
              errorTitle="Failed to load propagation rules"
              emptyTitle="No propagation rules yet"
              emptyDescription="Add a propagation rule to configure cloud propagation for this reflector."
            >
              <GenericTable
                columns={tableColumns as any}
                data={safeData}
                initialRowsPerPage={getInitialRowsPerPage(safeData.length)}
                rowsPerPageOptions={[...REFLECTOR_V2_ROWS_PER_PAGE_OPTIONS]}
                width="100%"
                maxTableBodyHeight={getTableBodyMaxHeight(CLOUD_PROPAGATION_CHROME_PX)}
              />
          </DataStateView>
        </div>
      </GenericCard>
      {/* Cloud Propagation Modal */}
      <CPModal
        key={selectedRow?.bgpNeighborId ?? (selectedRow as any)?.propagationRuleId ?? 'add-mode'}
        isOpen={isCPModalOpen}
        onClose={() => {
          setIsCPModalOpen(false);
          setSelectedRow(null);
        }}
        onSubmit={handleSubmit}
        initialValues={emptyCpInitialValues}
        tag={selectedRow?.mode}
        mode={selectedRow ? "edit" : "add"}
        bgpNeighborId={selectedRow?.bgpNeighborId}
        reflectorId={selectedRow?.reflectorInstanceId || reflectorId}
        isSubmitting={isSubmitting}
        hasExistingConfig={false}
        propagationRuleId={selectedRow ? (selectedRow as any).propagationRuleId : undefined}
        disableDynamicTagOptions={disableDynamicTagOptions}
        disableNexthopRetry={disableNexthopRetry}
        useNexthopVerification={useNexthopVerification}
        {...(clusterId && { clusterId })}
        {...(getAccessTokenSilently && { getAccessTokenSilently })}
        {...(selectedRow?.bgpNeighborIp && { neighborAddress: selectedRow.bgpNeighborIp })}
      />
      <GenericAlert
        isOpen={isAlertOpen}
        onClose={() => setIsAlertOpen(false)}
        onConfirm={() => setIsAlertOpen(false)}
        title={alertTitle}
        description={alertDescription}
        variant={alertVariant}
        confirmLabel="OK"
      />
      {/* Delete Confirmation Alert */}
      <GenericAlert
        isOpen={deleteConfirmState.isOpen}
        onClose={handleDeleteCancel}
        onConfirm={handleDeleteConfirm}
        title="Delete Propagation Rule"
        description={`Are you sure you want to delete the propagation rule${(deleteConfirmState.row as any)?.propagationRuleId ? ` (Sub ID: CP-${String((deleteConfirmState.row as any).propagationRuleId).padStart(5, '0')})` : deleteConfirmState.row?.bgpNeighborIp ? ` for BGP neighbor ${deleteConfirmState.row.bgpNeighborIp}` : ''}? This action cannot be undone.`}
        variant="error"
        confirmLabel="Delete"
        cancelLabel="Cancel"
      />
    </div>
  );
};

export default CloudPropagationPage;
